import { useState, useEffect, useRef } from "react";
import {
  useListDeals,
  useListSavedFilters,
  useCreateSavedFilter,
  useUpdateSavedFilter,
  useDeleteSavedFilter,
  useDeleteLocalDeal,
  getListSavedFiltersQueryKey,
  type SavedFilter,
} from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "wouter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency, formatDate } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  Plus,
  Building2,
  Calendar,
  Target,
  ClipboardCheck,
  Clock,
  AlertTriangle,
  Flame,
  Bookmark,
  BookmarkCheck,
  Share2,
  Lock,
  Pencil,
  Trash2,
  Check,
  X,
  ChevronDown,
  Save,
  MessageSquare,
  CheckCircle,
  XCircle,
  Cloud,
  FileText,
} from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { toastError } from "@/lib/toast-error";

type Staleness = "none" | "green" | "yellow" | "red" | null;

function StaleBadge({ daysSinceSent, daysSinceReply, staleness }: {
  daysSinceSent: number | null;
  daysSinceReply: number | null;
  staleness: Staleness;
}) {
  if (!staleness || staleness === "none") {
    return null;
  }
  const worstDays = Math.max(daysSinceSent ?? -1, daysSinceReply ?? -1);
  if (worstDays < 0) return null;

  const label = worstDays === 1 ? "1d" : `${worstDays}d`;
  const subLabel = daysSinceReply != null ? "since reply" : daysSinceSent != null ? "since sent" : "";

  if (staleness === "red") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 border border-red-200 dark:border-red-900 px-2 py-0.5 text-xs font-medium">
        <Flame className="h-3 w-3" />
        {label} {subLabel}
      </span>
    );
  }
  if (staleness === "yellow") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900 px-2 py-0.5 text-xs font-medium">
        <AlertTriangle className="h-3 w-3" />
        {label} {subLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 border border-green-200 dark:border-green-900 px-2 py-0.5 text-xs font-medium">
      <Clock className="h-3 w-3" />
      {label} {subLabel}
    </span>
  );
}

function staleRowClass(staleness: Staleness): string {
  if (staleness === "red") return "bg-red-50/40 dark:bg-red-950/10";
  if (staleness === "yellow") return "bg-yellow-50/30 dark:bg-yellow-950/10";
  return "";
}

type StaleMode = "none" | "any" | "red";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface LocalDealRow {
  id: string;
  accountName: string;
  opportunityName: string;
  ownerName?: string;
  ownerEmail?: string;
  closeDate?: string;
  mrr?: number;
  tcv?: number;
  approvalStatus?: string;
  sfOpportunityId?: string;
  supplierCount: number;
  createdAt: string;
  updatedAt: string;
}

function LocalStatusBadge({ status }: { status?: string }) {
  if (status === "approved") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900 gap-1">
        <CheckCircle className="h-3 w-3" /> Approved
      </Badge>
    );
  }
  if (status === "denied") {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900 gap-1">
        <XCircle className="h-3 w-3" /> Denied
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950/40 dark:text-yellow-400 dark:border-yellow-900 gap-1">
      <Clock className="h-3 w-3" /> Pending
    </Badge>
  );
}

function LocalDealsSection() {
  const queryClient = useQueryClient();
  const localDealsQueryKey = [`${BASE}/api/local-deals`];
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [pendingDelete, setPendingDelete] = useState<LocalDealRow | null>(null);

  const { data, isLoading } = useQuery<{ results: LocalDealRow[] }>({
    queryKey: localDealsQueryKey,
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/local-deals`, { credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error ?? `Failed (HTTP ${r.status})`);
      }
      return r.json();
    },
    staleTime: 30_000,
  });

  const deleteMutation = useDeleteLocalDeal({
    mutation: {
      onSuccess: () => {
        toast.success("Local deal deleted");
        queryClient.invalidateQueries({ queryKey: localDealsQueryKey });
        setPendingDelete(null);
      },
      onError: (err: unknown) => {
        toastError(err, "Failed to delete deal");
      },
    },
  });

  const allDeals = data?.results ?? [];
  const term = debouncedSearch.trim().toLowerCase();
  const deals = term
    ? allDeals.filter(
        (d) =>
          d.accountName.toLowerCase().includes(term) ||
          d.opportunityName.toLowerCase().includes(term),
      )
    : allDeals;

  const isEmptyOverall = !isLoading && allDeals.length === 0;

  return (
    <div className="space-y-4">
      {!isEmptyOverall && (
        <div className="flex items-center w-full max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search local deals..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="border rounded-xl bg-card p-4 space-y-3">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))
        ) : deals.length > 0 ? (
          deals.map((deal) => (
            <div key={deal.id} className="border rounded-xl bg-card p-4 space-y-3">
              <Link href={`/deals/local/${deal.id}`}>
                <div className="space-y-2 cursor-pointer">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{deal.opportunityName}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Building2 className="h-3 w-3" />
                        {deal.accountName}
                      </div>
                    </div>
                    <LocalStatusBadge status={deal.approvalStatus} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Value</div>
                      <div className="font-medium">
                        {deal.mrr ? (
                          <>{formatCurrency(deal.mrr)} <span className="text-xs text-muted-foreground font-normal">MRR</span></>
                        ) : deal.tcv ? (
                          <>{formatCurrency(deal.tcv)} <span className="text-xs text-muted-foreground font-normal">TCV</span></>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Close Date</div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {formatDate(deal.closeDate) || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Suppliers</div>
                      <div className="inline-flex items-center justify-center bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
                        {deal.supplierCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Created</div>
                      <div className="text-xs">{formatDate(deal.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </Link>
              <div className="flex items-center gap-2 pt-2 border-t">
                <Link href={`/deals/local/${deal.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    View Details
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-700 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950/30"
                  onClick={() => setPendingDelete(deal)}
                  aria-label={`Delete ${deal.opportunityName}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <LocalDealsEmpty hasSearch={Boolean(term)} totalCount={allDeals.length} />
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block border rounded-xl bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Opportunity / Account</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Close Date</TableHead>
              <TableHead>Suppliers</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : deals.length > 0 ? (
              deals.map((deal) => (
                <TableRow key={deal.id} className="hover:bg-muted/50 group transition-colors">
                  <TableCell>
                    <Link href={`/deals/local/${deal.id}`} className="block">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{deal.opportunityName}</span>
                          {deal.sfOpportunityId && (
                            <Badge variant="secondary" className="gap-1 text-xs">
                              <Cloud className="h-3 w-3" /> Synced
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {deal.accountName}
                        </span>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <LocalStatusBadge status={deal.approvalStatus} />
                  </TableCell>
                  <TableCell>
                    {deal.mrr ? (
                      <span className="font-medium">
                        {formatCurrency(deal.mrr)}{" "}
                        <span className="text-xs text-muted-foreground font-normal">MRR</span>
                      </span>
                    ) : deal.tcv ? (
                      <span className="font-medium">
                        {formatCurrency(deal.tcv)}{" "}
                        <span className="text-xs text-muted-foreground font-normal">TCV</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(deal.closeDate) || "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="inline-flex items-center justify-center bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
                      {deal.supplierCount}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{formatDate(deal.createdAt)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link href={`/deals/local/${deal.id}`}>
                        <Button variant="ghost" size="sm" className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          View
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        onClick={() => setPendingDelete(deal)}
                        aria-label={`Delete ${deal.opportunityName}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-48 text-center">
                  <LocalDealsEmpty hasSearch={Boolean(term)} totalCount={allDeals.length} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this local deal?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  This permanently deletes <span className="font-medium text-foreground">{pendingDelete.opportunityName}</span>{" "}
                  ({pendingDelete.accountName}) along with all of its supplier records and audit history. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete) deleteMutation.mutate({ id: pendingDelete.id });
              }}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LocalDealsEmpty({ hasSearch, totalCount }: { hasSearch: boolean; totalCount: number }) {
  if (hasSearch && totalCount > 0) {
    return (
      <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
        <Search className="h-8 w-8 mb-2 opacity-20" />
        <p>No local deals match your search.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
      <FileText className="h-10 w-10 mb-3 opacity-20" />
      <p className="font-medium text-foreground">No local deals yet</p>
      <p className="text-sm mt-1 max-w-sm text-center">
        Local deals let you register opportunities without a Salesforce record. Create one to get started.
      </p>
      <Link href="/deals/new-local" className="mt-4">
        <Button variant="outline" size="sm">
          <Plus className="mr-2 h-4 w-4" /> New Local Deal
        </Button>
      </Link>
    </div>
  );
}

export default function DealsList() {
  const queryClient = useQueryClient();
  const searchString = useSearch();
  const initializedRef = useRef(false);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [staleMode, setStaleModeState] = useState<StaleMode>("none");
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  const staleFilter = staleMode !== "none";

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const params = new URLSearchParams(searchString);
    const staleParam = params.get("stale");
    if (staleParam === "red") {
      setStaleModeState("red");
      setActiveViewId("__stale__");
    } else if (staleParam === "true") {
      setStaleModeState("any");
      setActiveViewId("__stale__");
    }
  }, [searchString]);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [saveViewShared, setSaveViewShared] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editShared, setEditShared] = useState(false);

  const staleApiParam = staleMode === "red" ? "red" : staleMode === "any" ? "true" : undefined;

  const { data: dealsData, isLoading } = useListDeals({
    q: debouncedSearch || undefined,
    stale: staleApiParam,
  });

  const { data: savedFiltersData, isLoading: isLoadingFilters } = useListSavedFilters();
  const createFilter = useCreateSavedFilter();
  const updateFilter = useUpdateSavedFilter();
  const deleteFilter = useDeleteSavedFilter();

  const builtinStaleView = { id: "__stale__", name: "Stale registrations", isShared: true, isOwn: false };

  function applyView(id: string) {
    if (id === "__stale__") {
      setStaleModeState("red");
      setSearch("");
      setActiveViewId("__stale__");
      return;
    }
    const filter = savedFiltersData?.results?.find((f) => f.id === id);
    if (!filter) return;
    const json = filter.filterJson as Record<string, unknown>;
    setSearch((json.search as string) ?? "");
    const savedStale = json.stale;
    if (savedStale === "red") setStaleModeState("red");
    else if (savedStale === true || savedStale === "true") setStaleModeState("any");
    else setStaleModeState("none");
    setActiveViewId(id);
  }

  function clearView() {
    setStaleModeState("none");
    setSearch("");
    setActiveViewId(null);
  }

  function getCurrentFilterJson(): Record<string, unknown> {
    return {
      search: search,
      stale: staleMode === "none" ? false : staleMode,
    };
  }

  function handleOpenSaveDialog() {
    setSaveViewName("");
    setSaveViewShared(false);
    setSaveDialogOpen(true);
  }

  function handleSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      toast.error("Enter a name for the view");
      return;
    }
    createFilter.mutate(
      {
        data: {
          name,
          isShared: saveViewShared,
          filterJson: getCurrentFilterJson(),
        },
      },
      {
        onSuccess: (newFilter) => {
          toast.success(`View "${name}" saved`);
          setSaveDialogOpen(false);
          setActiveViewId(newFilter.id);
          queryClient.invalidateQueries({ queryKey: getListSavedFiltersQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to save view");
        },
      },
    );
  }

  function handleOpenEditDialog(filter: SavedFilter) {
    setEditingFilter(filter);
    setEditName(filter.name);
    setEditShared(filter.isShared);
    setEditDialogOpen(true);
  }

  function handleUpdateFilter() {
    if (!editingFilter) return;
    const name = editName.trim();
    if (!name) {
      toast.error("Name cannot be empty");
      return;
    }
    updateFilter.mutate(
      { id: editingFilter.id, data: { name, isShared: editShared } },
      {
        onSuccess: () => {
          toast.success("View updated");
          setEditDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: getListSavedFiltersQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to update view");
        },
      },
    );
  }

  function handleDeleteFilter(filter: SavedFilter) {
    deleteFilter.mutate(
      { id: filter.id },
      {
        onSuccess: () => {
          toast.success(`View "${filter.name}" deleted`);
          if (activeViewId === filter.id) clearView();
          queryClient.invalidateQueries({ queryKey: getListSavedFiltersQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to delete view");
        },
      },
    );
  }

  const myFilters = savedFiltersData?.results?.filter((f) => f.isOwn) ?? [];
  const sharedFilters = savedFiltersData?.results?.filter((f) => !f.isOwn && f.isShared) ?? [];

  const hasActiveFilter = staleFilter || debouncedSearch;


  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deals</h1>
          <p className="text-muted-foreground">Manage your registered Salesforce opportunities.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link href="/deals/approvals">
            <Button variant="outline">
              <ClipboardCheck className="mr-2 h-4 w-4" /> Approvals
            </Button>
          </Link>
          <Link href="/deals/new-local">
            <Button variant="outline">
              <Plus className="mr-2 h-4 w-4" /> New Deal
            </Button>
          </Link>
          <Link href="/deals/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" /> Import from Salesforce
            </Button>
          </Link>
        </div>
      </div>

      <Tabs defaultValue="salesforce" className="w-full">
        <TabsList>
          <TabsTrigger value="salesforce" className="gap-1.5">
            <Cloud className="h-3.5 w-3.5" />
            Salesforce Deals
          </TabsTrigger>
          <TabsTrigger value="local" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Local Deals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salesforce" className="space-y-6 mt-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
        <div className="flex items-center w-full max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by account or opportunity..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setActiveViewId(null); }}
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              if (staleMode === "none") {
                setStaleModeState("red");
                setActiveViewId("__stale__");
              } else {
                setStaleModeState("none");
                if (activeViewId === "__stale__") setActiveViewId(null);
              }
            }}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              staleFilter
                ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            <Flame className="h-3.5 w-3.5" />
            Stale
          </button>

          {activeViewId && (
            <button
              onClick={clearView}
              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
            >
              <X className="h-3 w-3" />
              Clear filter
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {hasActiveFilter && (
            <Button variant="outline" size="sm" onClick={handleOpenSaveDialog}>
              <Save className="mr-1.5 h-3.5 w-3.5" />
              Save view
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Bookmark className="mr-1.5 h-3.5 w-3.5" />
                Views
                <ChevronDown className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                className={`flex items-center gap-2 ${activeViewId === "__stale__" ? "bg-accent" : ""}`}
                onSelect={() => applyView("__stale__")}
              >
                <Flame className="h-3.5 w-3.5 text-red-500" />
                <span className="flex-1 truncate">{builtinStaleView.name}</span>
                {activeViewId === "__stale__" && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
              </DropdownMenuItem>

              {myFilters.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">My views</div>
                  {myFilters.map((f) => (
                    <div key={f.id} className={`flex items-center group px-1 ${activeViewId === f.id ? "bg-accent" : ""}`}>
                      <DropdownMenuItem
                        className="flex-1 flex items-center gap-2 cursor-pointer"
                        onSelect={() => applyView(f.id)}
                      >
                        {f.isShared ? (
                          <Share2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className="flex-1 truncate">{f.name}</span>
                        {activeViewId === f.id && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
                      </DropdownMenuItem>
                      <button
                        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                        onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(f); }}
                        title="Edit view"
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground" />
                      </button>
                      <button
                        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                        onClick={(e) => { e.stopPropagation(); handleDeleteFilter(f); }}
                        title="Delete view"
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </>
              )}

              {sharedFilters.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Shared with org</div>
                  {sharedFilters.map((f) => (
                    <DropdownMenuItem
                      key={f.id}
                      className={`flex items-center gap-2 ${activeViewId === f.id ? "bg-accent" : ""}`}
                      onSelect={() => applyView(f.id)}
                    >
                      <Share2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{f.name}</span>
                      {activeViewId === f.id && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {!isLoadingFilters && myFilters.length === 0 && sharedFilters.length === 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2 text-xs text-muted-foreground text-center">
                    No saved views yet. Apply filters and click "Save view".
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {activeViewId && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BookmarkCheck className="h-4 w-4 text-primary" />
          <span>
            View:{" "}
            <span className="font-medium text-foreground">
              {activeViewId === "__stale__"
                ? "Stale registrations"
                : savedFiltersData?.results?.find((f) => f.id === activeViewId)?.name ?? "Saved view"}
            </span>
          </span>
        </div>
      )}

      {/* Mobile card layout */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="border rounded-xl bg-card p-4 space-y-3">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))
        ) : dealsData?.results && dealsData.results.length > 0 ? (
          dealsData.results.map((deal) => (
            <Link key={deal.id} href={`/deals/${deal.id}`}>
              <div className={`border rounded-xl bg-card p-4 space-y-3 hover:bg-muted/50 transition-colors cursor-pointer ${staleRowClass(deal.staleness as Staleness)}`}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{deal.opportunityName}</span>
                    {deal.hasNewReply && (
                      <Badge variant="secondary" className="gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                        <MessageSquare className="h-3 w-3" /> New Reply
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Building2 className="h-3 w-3" />
                    {deal.accountName}
                  </div>
                  {deal.staleness && deal.staleness !== "none" && (
                    <div className="mt-1.5">
                      <StaleBadge
                        daysSinceSent={deal.daysSinceSent ?? null}
                        daysSinceReply={deal.daysSinceReply ?? null}
                        staleness={deal.staleness as Staleness}
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Value</div>
                    <div className="font-medium">
                      {deal.mrr ? (
                        <>{formatCurrency(deal.mrr)} <span className="text-xs text-muted-foreground font-normal">MRR</span></>
                      ) : deal.tcv ? (
                        <>{formatCurrency(deal.tcv)} <span className="text-xs text-muted-foreground font-normal">TCV</span></>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Close Date</div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDate(deal.closeDate) || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Registrations</div>
                    <div className="inline-flex items-center justify-center bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
                      {deal.registrationCount || 0}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full">
                  View Details
                </Button>
              </div>
            </Link>
          ))
        ) : (
          <div className="border rounded-xl bg-card p-8 flex flex-col items-center justify-center text-muted-foreground">
            <Target className="h-8 w-8 mb-2 opacity-20" />
            <p>No deals found.</p>
            {(search || staleFilter) && <p className="text-sm mt-1">Try adjusting your filters.</p>}
          </div>
        )}
      </div>

      <div className="hidden sm:block border rounded-xl bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Opportunity / Account</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Close Date</TableHead>
              <TableHead>Registrations</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : dealsData?.results && dealsData.results.length > 0 ? (
              dealsData.results.map((deal) => (
                <TableRow key={deal.id} className={`hover:bg-muted/50 group transition-colors ${staleRowClass(deal.staleness as Staleness)}`}>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{deal.opportunityName}</span>
                        {deal.hasNewReply && (
                          <Badge variant="secondary" className="gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                            <MessageSquare className="h-3 w-3" /> New Reply
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Building2 className="h-3 w-3" />
                        {deal.accountName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StaleBadge
                      daysSinceSent={deal.daysSinceSent ?? null}
                      daysSinceReply={deal.daysSinceReply ?? null}
                      staleness={deal.staleness as Staleness}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      {deal.mrr ? (
                        <span className="font-medium">{formatCurrency(deal.mrr)} <span className="text-xs text-muted-foreground font-normal">MRR</span></span>
                      ) : deal.tcv ? (
                        <span className="font-medium">{formatCurrency(deal.tcv)} <span className="text-xs text-muted-foreground font-normal">TCV</span></span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(deal.closeDate) || "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="inline-flex items-center justify-center bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
                      {deal.registrationCount || 0}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link href={`/deals/${deal.id}`}>
                      <Button variant="ghost" size="sm" className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        View Details
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-48 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Target className="h-8 w-8 mb-2 opacity-20" />
                    <p>{staleFilter ? "No stale deals found." : "No deals found."}</p>
                    {(search || staleFilter) && <p className="text-sm mt-1">Try adjusting your filters.</p>}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
        </TabsContent>

        <TabsContent value="local" className="space-y-6 mt-4">
          <LocalDealsSection />
        </TabsContent>
      </Tabs>

      {/* Save view dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="view-name">View name</Label>
              <Input
                id="view-name"
                placeholder="e.g. Awaiting supplier &gt; 7 days"
                value={saveViewName}
                onChange={(e) => setSaveViewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveView(); }}
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="view-shared" className="text-sm font-medium">Share with org</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Visible to everyone in your organization.</p>
              </div>
              <Switch
                id="view-shared"
                checked={saveViewShared}
                onCheckedChange={setSaveViewShared}
              />
            </div>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <span className="font-medium">Active filters:</span>{" "}
              {search ? `Search: "${search}"` : ""}
              {search && staleFilter ? " · " : ""}
              {staleFilter ? "Stale deals only" : ""}
              {!search && !staleFilter ? "None" : ""}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveView} disabled={createFilter.isPending || !saveViewName.trim()}>
              {createFilter.isPending ? "Saving…" : "Save view"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit view dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit view</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-view-name">View name</Label>
              <Input
                id="edit-view-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleUpdateFilter(); }}
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="edit-view-shared" className="text-sm font-medium">Share with org</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Visible to everyone in your organization.</p>
              </div>
              <Switch
                id="edit-view-shared"
                checked={editShared}
                onCheckedChange={setEditShared}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateFilter} disabled={updateFilter.isPending || !editName.trim()}>
              {updateFilter.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
